import React, { useState } from "react";
import { Download, FileSpreadsheet, FileText, Table2, Loader2 } from "lucide-react";
import { API } from "../lib/api";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { toast } from "sonner";

const FORMATS = [
  { ext: "xlsx", label: "Excel (.xlsx)", hint: "All columns, with totals", icon: FileSpreadsheet },
  { ext: "pdf", label: "PDF (.pdf)", hint: "Landscape summary table", icon: FileText },
  { ext: "csv", label: "CSV (.csv)", hint: "Raw data for other tools", icon: Table2 },
];

// Manager-only export of the currently filtered records. `resource` is
// "contacts" or "deals"; `params` mirrors the filters shown on screen.
export default function ExportMenu({ resource, params = {}, label = "Export" }) {
  const [busy, setBusy] = useState("");

  const run = async (ext) => {
    setBusy(ext);
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== "" && v != null)
    ).toString();
    try {
      const res = await fetch(`${API}/export/${resource}.${ext}${qs ? `?${qs}` : ""}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${resource}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${resource === "deals" ? "Deals" : "Contacts"} exported as ${ext.toUpperCase()}`);
    } catch (err) {
      toast.error(err.message || "Export failed");
    }
    setBusy("");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" data-testid={`export-${resource}`}>
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">
          Download current view
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {FORMATS.map((f) => (
          <DropdownMenuItem key={f.ext} data-testid={`export-${resource}-${f.ext}`}
            disabled={!!busy} onSelect={() => run(f.ext)} className="cursor-pointer">
            <f.icon className="w-4 h-4 mr-2 text-primary" />
            <span className="flex-1">
              <span className="block text-sm">{f.label}</span>
              <span className="block text-[10px] text-muted-foreground">{f.hint}</span>
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
